import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, FormControlLabel, Switch
} from '@mui/material';
import { Home, Add, Edit, Delete, Warehouse, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const emptyForm = {
  locationCode: '', locationName: '', description: '', address: '', isActive: true
};

const LocationList = () => {
  const navigate = useNavigate();
  const [locations,    setLocations]    = useState([]);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(true);
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [editingCode,  setEditingCode]  = useState(null);
  const [form,         setForm]         = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });

  // Deactivate confirm dialog
  const [deactivateDlg, setDeactivateDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchLocations = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/payables/locations`, { headers }
      );
      setLocations(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLocations(); }, []); // eslint-disable-line

  const handleCreate = () => { setEditingCode(null); setForm(emptyForm); setDialogOpen(true); };

  const handleEdit = (loc) => {
    setEditingCode(loc.locationCode);
    setForm({ ...emptyForm, ...loc });
    setDialogOpen(true);
  };

  const confirmAction = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/payables/locations/${deactivateDlg}`, { headers }
      );
      setDeactivateDlg(null);
      setError('Location deactivated successfully');
      fetchLocations();
    } catch (err) {
      setDeactivateDlg(null);
      setError(err.response?.data?.error || 'Failed to deactivate location');
    }
  };

  const handleSave = async () => {
    if (!editingCode && !form.locationCode.trim()) { setError('Location Code is required'); return; }
    if (!form.locationName.trim()) { setError('Location Name is required'); return; }
    try {
      if (editingCode) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/payables/locations/${editingCode}`,
          form, { headers }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/payables/locations`,
          form, { headers }
        );
      }
      setDialogOpen(false);
      setError('');
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save location');
    }
  };

  const field = (label, key, extra = {}) => (
    <TextField label={label} value={form[key]}
      onChange={e => setForm({ ...form, [key]: e.target.value })}
      fullWidth size="small" {...extra} />
  );

  if (loading) return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading locations...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Warehouse sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Locations</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/setups')} title="Back to Setups"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={handleCreate} title="Add Location"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {error && (
            <Alert severity={error.includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {locations.length === 0 ? (
            <Typography color="text.secondary">No locations set up yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map(loc => (
                  <TableRow key={loc.locationCode} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {loc.locationCode}
                    </TableCell>
                    <TableCell>{loc.locationName}</TableCell>
                    <TableCell>{loc.description}</TableCell>
                    <TableCell>{loc.address}</TableCell>
                    <TableCell>
                      <Chip
                        label={loc.isActive === false ? 'Inactive' : 'Active'}
                        color={loc.isActive === false ? 'default' : 'success'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small" onClick={() => handleEdit(loc)} sx={{ mr: 0.5 }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton color="warning" size="small"
                        onClick={() => setDeactivateDlg(loc.locationCode)}
                        disabled={loc.isActive === false}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Deactivate Confirm Dialog */}
      <Dialog open={Boolean(deactivateDlg)} onClose={() => setDeactivateDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmber color="warning" /> Deactivate Location
        </DialogTitle>
        <DialogContent>
          <Typography>Deactivate location <strong>{deactivateDlg}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmAction}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingCode ? `Edit Location: ${editingCode}` : 'New Location'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {field('Location Code', 'locationCode', { disabled: !!editingCode })}
            {field('Location Name', 'locationName')}
            {field('Description',   'description')}
            {field('Address',       'address')}
            {editingCode && (
              <FormControlLabel
                control={<Switch checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
                label="Active" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationList;
